import React, { useMemo } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { CheckOutlined } from "@ant-design/icons";
import { CURRENCY, DELIVERY_REGION } from "../../constants/DeliveryConstants";
import { useDeliveryAddress } from "../../contexts/DeliveryAddressProvider";
import { colors } from "../../utils/theme";
import { deliveryRegionFor, getFreeDeliveryFromPrice } from "../../helpers/region";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";

const UspBar = () => {
  const { t } = useTranslation();
  const { values, openModal } = useDeliveryAddress();

  // De drempel volgt de postcode (HVC-83): EUR 100 in West-Friesland, waar de
  // eigen bus rijdt, EUR 150 daarbuiten en zolang de postcode onbekend is.
  const threshold = useMemo(() => {
    return getFreeDeliveryFromPrice(values?.zip, values?.country);
  }, [values?.zip, values?.country]);

  const regional = useMemo(
    () =>
      deliveryRegionFor({ zip: values?.zip, country: values?.country }) ===
      DELIVERY_REGION.REGIONAL,
    [values?.zip, values?.country],
  );

  /*
    Geen land-forks meer. Hier stonden er twee:

    - een Belgie-USP "Lagere verzendkosten boven €100", die nooit ergens in de
      prijsberekening bestond en dus altijd een loze belofte was;
    - een gate die de gratis-verzending-USP alleen aan Nederland toonde, omdat
      Belgie destijds nooit gratis bezorgd kreeg.

    Belgie heeft dezelfde drempel als de rest van het land, dus de USP mag
    altijd mee; alleen West-Friesland heeft een eigen (lagere) drempel.
  */
  const items = [
    {
      key: "free",
      text: t("header_usps.free_shipping", {
        amount: `${CURRENCY}${threshold}`,
      }),
      onClick: async () => {
        try {
          await openModal();
          // threshold will re-compute from context automatically
        } catch (e) {}
      },
      clickable: true,
    },
    {
      key: "frozen",
      text: t("header_usps.frozen_delivery"),
    },
    {
      key: "nextday",
      // Regionaal is er geen 12:00-grens: voor 23:59 besteld, morgen (wo-vr) in huis.
      text: t(
        regional
          ? "header_usps.next_day_delivery_regional"
          : "header_usps.next_day_delivery",
      ),
    },
  ];

  return (
    <BannerWrapper aria-label="USP banner">
      <SwiperWrapper
        spaceBetween={0}
        slidesPerView={1}
        breakpoints={{
          768: { slidesPerView: 3 },
        }}
        loop
        autoplay={{ delay: 3500, disableOnInteraction: false }}
        modules={[Autoplay]}
        aria-label="USP slider"
      >
        {items.map((item) => (
          <SwiperSlide key={item.key}>
            <Usp
              as={item.onClick ? "button" : "div"}
              onClick={item.onClick || undefined}
              $clickable={!!item.onClick}
            >
              <CheckOutlined className="icon" style={{ margin: 0 }} />
              <span>{item.text}</span>
            </Usp>
          </SwiperSlide>
        ))}
      </SwiperWrapper>
    </BannerWrapper>
  );
};

export default UspBar;

const BannerWrapper = styled.div`
  width: 100%;
  background: ${colors.primary};
  color: #fff;
`;

const SwiperWrapper = styled(Swiper)`
  min-height: 38px;
  padding: 6px 0;
  align-content: center;

  .swiper-wrapper {
    align-items: center;
  }

  .swiper-slide {
    display: flex;
    justify-content: center;
  }
`;

const Usp = styled.button`
  appearance: none;
  border: 0;
  background: transparent;
  color: inherit;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 1.3;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")} !important;
  padding: 4px 10px;

  .icon {
    font-size: 14px;
  }
`;
